import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Save, Trash2, Users, X, Edit3, Sparkles } from "lucide-react";

const PLAN_TYPES = [
  { value: "hours",     label: "Hours Pack" },
  { value: "overs",     label: "Overs Pack" },
  { value: "sessions",  label: "Sessions Pack" },
  { value: "unlimited", label: "Unlimited (time-based)" },
];

const EMPTY = {
  name: "", facility_slug: "badminton", plan_type: "hours",
  quota: 20, price: 5000, duration_days: 90, description: "",
  benefits: [], active: true, guest_limit: 0, max_units_per_day: null,
};

export default function AdminMembershipPlans() {
  const [plans, setPlans] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const reload = async () => {
    setLoading(true);
    const [p, f] = await Promise.all([
      api.get("/admin/membership-plans").then((r) => r.data),
      api.get("/admin/facility-membership-flags").then((r) => r.data),
    ]);
    setPlans(p || []); setFlags(f || []);
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const toggleFacility = async (slug, current) => {
    // Optimistic
    setFlags((arr) => arr.map((x) => x.slug === slug ? { ...x, membership_enabled: !current } : x));
    try {
      await api.post(`/admin/facility-membership-flags/${slug}?enabled=${!current}`);
      toast.success(!current
        ? `Memberships now VISIBLE for ${slug} on public catalog`
        : `Memberships HIDDEN for ${slug} — visitors can't buy this facility's plans`);
    } catch (e) {
      setFlags((arr) => arr.map((x) => x.slug === slug ? { ...x, membership_enabled: current } : x));
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };

  const save = async (form) => {
    try {
      if (form.id) {
        await api.put(`/admin/membership-plans/${form.id}`, form);
        toast.success("Plan updated");
      } else {
        await api.post("/admin/membership-plans", form);
        toast.success("Plan created");
      }
      setEdit(null); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? Active memberships will keep working but plan will be hidden from catalog.`)) return;
    try {
      const { data } = await api.delete(`/admin/membership-plans/${p.id}`);
      toast.success(data.soft_deleted ? `Plan soft-deleted (${data.active_members} active members kept safe)` : "Plan deleted");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-5 md:p-8" data-testid="admin-membership-plans">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest brand-orange">// Facility Memberships</div>
          <h1 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter mt-1">Membership Pricing</h1>
          <p className="text-sm text-white/60 mt-1">Set prices per facility, plan type, quota & validity. Members see this on the public catalog.</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} data-testid="new-plan-btn"
          className="bg-brand-orange text-black px-4 py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center gap-2">
          <Plus size={14} /> New Plan
        </button>
      </div>

      {/* Per-facility membership availability toggle strip */}
      <div className="mb-6 border border-white/12 bg-[#131A2B] p-4" data-testid="facility-membership-strip">
        <div className="font-mono text-[10px] uppercase tracking-widest brand-orange mb-3">// Facility availability for membership purchase</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          {flags.map((f) => (
            <button
              key={f.slug}
              onClick={() => toggleFacility(f.slug, f.membership_enabled)}
              disabled={!f.facility_active}
              data-testid={`fac-toggle-${f.slug}`}
              title={f.membership_enabled ? "Members can buy plans for this facility" : "HIDDEN — visitors can't buy plans for this facility"}
              className={`text-left p-3 border transition ${
                !f.facility_active ? "border-white/8 opacity-40 cursor-not-allowed" :
                f.membership_enabled ? "border-brand-blue/50 bg-brand-blue/5 hover:bg-brand-blue/10" :
                "border-red-500/50 bg-red-950/20 hover:bg-red-950/30"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-display font-black uppercase text-sm truncate">{f.name}</div>
                <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                  f.membership_enabled ? "border-brand-blue/50 brand-blue" : "border-red-500/50 text-red-400"
                }`}>{f.membership_enabled ? "ON" : "OFF"}</span>
              </div>
              {!f.facility_active && (
                <div className="text-[10px] text-white/40 mt-1">Facility itself is hidden</div>
              )}
            </button>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-white/40">
          Toggling OFF hides all plans for that facility from the public /membership catalog. Existing memberships keep working.
        </div>
      </div>

      {loading ? <div className="text-white/40 text-center py-16 font-mono uppercase tracking-widest text-xs">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
          {plans.map((p) => (
            <div key={p.id} className={`border ${p.active ? "border-white/12 bg-[#131A2B]" : "border-red-500/30 bg-red-950/10"} p-5`} data-testid={`plan-row-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{p.facility_name || p.facility_slug}</div>
                  <div className="font-display text-lg font-black uppercase mt-1">{p.name}</div>
                </div>
                <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${p.active ? "border-green-500/50 text-green-400" : "border-red-500/50 text-red-400"}`}>
                  {p.active ? "Active" : "Hidden"}
                </div>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <div className="font-display text-3xl font-black brand-blue">{formatINR(p.price)}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">/ {p.duration_days}d</div>
              </div>
              <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-white/60">
                {p.plan_type === "unlimited" ? "Unlimited use" : `${p.quota} ${p.plan_type}`}
              </div>
              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/60">
                <Users size={12} className="brand-blue" />
                <span data-testid={`plan-members-${p.id}`}>{p.members_count || 0} active members</span>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEdit(p)} data-testid={`edit-plan-${p.id}`} className="flex-1 border border-white/15 py-2 text-[11px] font-mono uppercase tracking-widest hover:border-brand-blue inline-flex items-center justify-center gap-1"><Edit3 size={11} /> Edit</button>
                <button onClick={() => del(p)} className="border border-red-500/40 text-red-400 px-3 py-2 hover:bg-red-500 hover:text-white"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="col-span-full border border-dashed border-white/15 p-10 text-center text-white/50 text-sm">
              No plans yet. Click &quot;New Plan&quot; to add one for badminton, indoor-box, indoor-net, bowling-machine, gym or swimming.
            </div>
          )}
        </div>
      )}

      {edit && <EditModal plan={edit} facilities={flags} onClose={() => setEdit(null)} onSave={save} />}
    </div>
  );
}

function EditModal({ plan, facilities = [], onClose, onSave }) {
  const [form, setForm] = useState({ ...plan, benefits: plan.benefits || [] });
  const [benefit, setBenefit] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const isUnlimited = form.plan_type === "unlimited";
    onSave({
      ...form,
      quota: isUnlimited ? null : Number(form.quota || 0),
      price: Number(form.price || 0),
      duration_days: Number(form.duration_days || 30),
      max_units_per_day: (isUnlimited || !form.max_units_per_day)
        ? null
        : Math.max(1, Number(form.max_units_per_day)),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="w-full max-w-lg bg-[#131A2B] border border-white/12 my-8" onClick={(e) => e.stopPropagation()} data-testid="plan-edit-modal">
        <div className="p-5 border-b border-white/10 flex justify-between items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// {plan.id ? "Edit Plan" : "New Plan"}</div>
            <div className="font-display text-lg font-black uppercase mt-0.5">{plan.id ? "Update pricing" : "Create membership plan"}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Row label="Plan name" testid="plan-name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Badminton · 20 Hours"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Facility" testid="plan-facility">
              <select value={form.facility_slug} onChange={(e) => setForm({ ...form, facility_slug: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                {(facilities.length ? facilities : [{ slug: form.facility_slug, name: form.facility_slug }]).map((f) => (
                  <option key={f.slug} value={f.slug}>{f.name}</option>
                ))}
              </select>
            </Row>
            <Row label="Plan type" testid="plan-type">
              <select value={form.plan_type} onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm">
                {PLAN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Row>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Row label="Price (₹)" testid="plan-price">
              <input required type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
            </Row>
            <Row label={form.plan_type === "unlimited" ? "Quota (N/A)" : `Quota (${form.plan_type})`} testid="plan-quota">
              <input type="number" min="0" value={form.quota ?? ""} disabled={form.plan_type === "unlimited"}
                onChange={(e) => setForm({ ...form, quota: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm disabled:opacity-40" />
            </Row>
            <Row label="Valid (days)" testid="plan-duration">
              <input required type="number" min="1" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
            </Row>
          </div>
          <Row label="Description" testid="plan-description">
            <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm" />
          </Row>
          <Row label="Guest allowance (per visit)" testid="plan-guest-limit">
            <div className="flex items-center gap-2">
              <input type="number" min="-1" value={form.guest_limit ?? 0}
                onChange={(e) => setForm({ ...form, guest_limit: e.target.value === "" ? 0 : Number(e.target.value) })}
                className="w-24 bg-black/40 border border-white/15 px-3 py-2 text-sm" />
              <span className="text-[11px] text-white/50">0 = no guests · positive = fixed cap · -1 = unlimited</span>
            </div>
          </Row>
          <Row label={`Daily cap (${form.plan_type === "unlimited" ? "N/A · unlimited plan" : `max ${form.plan_type} per day`})`} testid="plan-max-per-day">
            <div className="flex items-center gap-2">
              <input type="number" min="0" value={form.max_units_per_day ?? ""}
                disabled={form.plan_type === "unlimited"}
                onChange={(e) => setForm({ ...form, max_units_per_day: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="e.g. 2"
                className="w-24 bg-black/40 border border-white/15 px-3 py-2 text-sm disabled:opacity-40" />
              <span className="text-[11px] text-white/50">Blank / 0 = no daily cap. Prevents burning the whole quota in one day.</span>
            </div>
          </Row>
          <Row label="Benefits (member perks)" testid="plan-benefits">
            <div className="flex gap-2">
              <input value={benefit} onChange={(e) => setBenefit(e.target.value)}
                placeholder="e.g. Priority booking window"
                className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm" />
              <button type="button" onClick={() => {
                if (benefit.trim()) { setForm({ ...form, benefits: [...form.benefits, benefit.trim()] }); setBenefit(""); }
              }} className="border border-white/15 px-3 hover:border-brand-blue"><Plus size={14} /></button>
            </div>
            {form.benefits.length > 0 && (
              <ul className="mt-2 space-y-1">
                {form.benefits.map((b, i) => (
                  <li key={i} className="flex items-center justify-between border border-white/10 px-3 py-1.5 text-xs">
                    <span className="flex items-center gap-2"><Sparkles size={11} className="brand-orange" />{b}</span>
                    <button type="button" onClick={() => setForm({ ...form, benefits: form.benefits.filter((_, j) => j !== i) })} className="text-red-400"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </Row>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="plan-active-toggle" />
            <span>Active (visible on public catalog)</span>
          </label>
        </div>
        <div className="p-5 border-t border-white/10 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 border border-white/15 py-2.5 font-mono text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" data-testid="plan-save-btn" className="flex-1 bg-brand-orange text-black py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white inline-flex items-center justify-center gap-2">
            <Save size={12} /> {plan.id ? "Save" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
